import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Inbox, Lock, MessageSquareReply, Send, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMe } from "@/context/MeContext";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/dates";
import { Card, Empty, ErrorBox, Loading, PageTitle } from "@/components/common";
import { Field, OptionSelect } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORIES = ["Segnalazione", "Comunicazione", "Proposta", "Altro"];

function Compose({ onSent }) {
  const api = useApi();
  const blank = { category: "Segnalazione", subject: "", body: "", anonymous: false };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return toast.error("Scrivi oggetto e messaggio");
    setBusy(true);
    try {
      await api("post", "/messages", form);
      toast.success(form.anonymous ? "Messaggio anonimo inviato" : "Messaggio inviato ai rappresentanti");
      setForm(blank);
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="fade-up">
      <form onSubmit={send} className="space-y-4" data-testid="message-form">
        <Field label="Tipo"><OptionSelect value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testId="message-category" /></Field>
        <Field label="Oggetto"><Input className="h-11" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="message-subject" /></Field>
        <Field label="Messaggio"><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="message-body" /></Field>
        <label className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 transition-colors ${form.anonymous ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-secondary/60"}`}>
          <span className="flex items-start gap-2.5">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="block text-sm font-semibold">Invia in anonimo</span>
              <span className="block text-xs opacity-75">Il tuo nome non viene salvato: nessuno, nemmeno i rappresentanti, saprà chi l'ha scritto.</span>
            </span>
          </span>
          <Switch checked={form.anonymous} onCheckedChange={(v) => setForm({ ...form, anonymous: v })} data-testid="toggle-anonymous-mode" />
        </label>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="btn-send-message"><Send className="mr-2 h-4 w-4" />Invia ai rappresentanti</Button>
      </form>
    </Card>
  );
}

const MessageHead = ({ m }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-bold uppercase">{m.category}</span>
    {m.anonymous
      ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white dark:bg-slate-100 dark:text-slate-900"><Lock className="h-3 w-3" />Anonimo</span>
      : <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"><UserRound className="h-3.5 w-3.5" />{m.sender_name}</span>}
    <span className="ml-auto text-xs text-muted-foreground">{fmtDateTime(m.created_at)}</span>
  </div>
);

const Reply = ({ m }) => m.reply && (
  <div className="mt-3 rounded-xl border-l-4 border-primary bg-primary/5 p-3 text-sm">
    <p className="text-xs font-bold text-primary">Risposta di {m.replied_by}</p>
    <p className="mt-0.5 whitespace-pre-line">{m.reply}</p>
  </div>
);

function InboxItem({ m, onUpdate }) {
  const api = useApi();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const expand = async () => {
    setOpen(!open);
    if (!m.read) {
      try { await api("patch", `/messages/${m.id}/read`); onUpdate({ ...m, read: true }); } catch (e) { /* stays unread */ }
    }
  };
  const reply = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { onUpdate(await api("post", `/messages/${m.id}/reply`, { reply: text })); setText(""); toast.success("Risposta inviata"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className={!m.read ? "border-primary/50" : ""} data-testid={`inbox-message-${m.id}`}>
      <button className="w-full text-left" onClick={expand} data-testid={`inbox-message-open-${m.id}`}>
        <MessageHead m={m} />
        <p className="mt-2 flex items-center gap-2 font-heading font-bold">{!m.read && <span className="h-2 w-2 rounded-full bg-primary" data-testid={`inbox-unread-dot-${m.id}`} />}{m.subject}</p>
        <p className={`mt-1 whitespace-pre-line text-sm ${open ? "" : "line-clamp-2"}`}>{m.body}</p>
      </button>
      <Reply m={m} />
      {open && (m.anonymous ? (
        <p className="mt-3 text-xs text-muted-foreground">Messaggio anonimo: non è possibile rispondere.</p>
      ) : (
        <div className="mt-3 space-y-2">
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi una risposta…" data-testid={`inbox-reply-text-${m.id}`} />
          <Button size="sm" className="rounded-full" disabled={busy} onClick={reply} data-testid={`inbox-reply-send-${m.id}`}><MessageSquareReply className="mr-1.5 h-4 w-4" />Rispondi</Button>
        </div>
      ))}
    </Card>
  );
}

function SentList({ data }) {
  if (!data) return <Loading />;
  if (data.length === 0) return <Empty text="Non hai ancora inviato messaggi firmati." testId="sent-empty" />;
  return (
    <div className="space-y-3">
      {data.map((m) => (
        <Card key={m.id} data-testid={`sent-message-${m.id}`}>
          <MessageHead m={m} />
          <p className="mt-2 font-heading font-bold">{m.subject}</p>
          <p className="mt-1 whitespace-pre-line text-sm">{m.body}</p>
          <Reply m={m} />
        </Card>
      ))}
    </div>
  );
}

export default function Messaggi() {
  const { isAdmin } = useMe();
  const navigate = useNavigate();
  const sent = useFetch("/messages?box=sent");
  const inbox = useFetch(isAdmin ? "/messages?box=inbox" : "/messages?box=sent");
  const unread = isAdmin && inbox.data ? inbox.data.filter((m) => !m.read).length : 0;
  if (sent.error) return <ErrorBox text={sent.error} onRetry={sent.reload} />;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-3 flex min-h-11 items-center gap-1 text-sm font-semibold text-muted-foreground" data-testid="messages-back-btn"><ArrowLeft className="h-4 w-4" />Indietro</button>
      <PageTitle title="Messaggi ai rappresentanti" subtitle="Segnala o comunica qualcosa, anche in anonimo" />
      <Tabs defaultValue={isAdmin ? "inbox" : "write"}>
        <TabsList className={`mb-5 grid h-11 w-full rounded-full sm:max-w-md ${isAdmin ? "grid-cols-3" : "grid-cols-2"}`}>
          {isAdmin && <TabsTrigger value="inbox" className="rounded-full" data-testid="messages-tab-inbox">Ricevuti{unread > 0 && ` (${unread})`}</TabsTrigger>}
          <TabsTrigger value="write" className="rounded-full" data-testid="messages-tab-write">Scrivi</TabsTrigger>
          <TabsTrigger value="sent" className="rounded-full" data-testid="messages-tab-sent">Inviati</TabsTrigger>
        </TabsList>
        {isAdmin && (
          <TabsContent value="inbox">
            {!inbox.data ? <Loading /> : inbox.data.length === 0 ? <Empty icon={Inbox} text="Nessun messaggio ricevuto." testId="inbox-empty" /> : (
              <div className="space-y-3">{inbox.data.map((m) => <InboxItem key={m.id} m={m} onUpdate={(u) => inbox.setData((l) => l.map((x) => (x.id === u.id ? u : x)))} />)}</div>
            )}
          </TabsContent>
        )}
        <TabsContent value="write" className="lg:max-w-2xl"><Compose onSent={() => { sent.reload(); if (isAdmin) inbox.reload(); }} /></TabsContent>
        <TabsContent value="sent">
          <p className="mb-3 text-xs text-muted-foreground">I messaggi anonimi non sono collegati a te, quindi non compaiono qui.</p>
          <SentList data={sent.data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
